import React, { useState, useEffect, useContext } from 'react';
import api from '../api';
import { AuthContext } from '../AuthContext';
import { formatLocal } from '../utils/timezones';
import { useHOA } from '../HOAApp';

const LedgerTab = () => {
  const { user } = useContext(AuthContext);
  const { hoaData } = useHOA();
  const hoaTimezone = hoaData?.timezone || 'US/Central';
  const [properties, setProperties] = useState([]);
  const [allLedgers, setAllLedgers] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'single'
  const [ledger, setLedger] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showGenerateStatementsModal, setShowGenerateStatementsModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [newProperty, setNewProperty] = useState({
    property_address: '',
    property_identifier: '',
    account_holder_names: ['', '', ''],
    notes: ''
  });
  const [statementConfig, setStatementConfig] = useState({
    statement_type: 'upcoming_dues',
    selected_properties: [],
    custom_notes: '',
    allow_zero_line_items: false  // New: allow generating statements for properties with 0 line items
  });
  const [editingAdditionalName, setEditingAdditionalName] = useState(null);
  const [additionalNameValue, setAdditionalNameValue] = useState('');
  const [editingMailingAddress, setEditingMailingAddress] = useState(null);
  const [mailingAddressValue, setMailingAddressValue] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [editingPropertyIdentifier, setEditingPropertyIdentifier] = useState(null);
  const [propertyIdentifierValue, setPropertyIdentifierValue] = useState('');
  const [showBalanceForwardModal, setShowBalanceForwardModal] = useState(false);
  const [balanceForwardData, setBalanceForwardData] = useState({
    property_address: '',
    amount: '',
    description: 'Balance Forward',
    due_date: new Date().toISOString().split('T')[0]
  });
  const [editingBalance, setEditingBalance] = useState(null); // {property_address, field}
  const [balanceEditValue, setBalanceEditValue] = useState('');
  const [importValidation, setImportValidation] = useState(null);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [exportType, setExportType] = useState('summary'); // 'summary' or 'detailed'
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [checkingMerges, setCheckingMerges] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetType, setResetType] = useState('all');
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetBackups, setResetBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);

  const loadResetBackups = async () => {
    try {
      setLoadingBackups(true);
      const { data } = await api.get('/api/ledger/reset-backups');
      setResetBackups(data.backups || []);
    } catch (err) {
      console.error('Failed to load reset backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  };
  
  // Manual consolidation selection
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [consolidationPreview, setConsolidationPreview] = useState(null);
  const [selectedForConsolidation, setSelectedForConsolidation] = useState([]);
  
  // Manual merge (completely manual selection)
  const [showManualMergeModal, setShowManualMergeModal] = useState(false);
  const [allLedgerProperties, setAllLedgerProperties] = useState([]);
  const [sourceProperty, setSourceProperty] = useState(null);
  const [targetProperty, setTargetProperty] = useState(null);
  
  // Expandable rows state
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [expandedLedger, setExpandedLedger] = useState(null);
  const [loadingExpanded, setLoadingExpanded] = useState(false);

  const isBoard = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary', 'platform_admin'].includes(user?.role?.toLowerCase());

  // Helper function to calculate late fee date preview
  const calculateLateFeeDate = (daysAfterDue) => {
    if (!paymentConfig) return null;
    
    const dueDay = paymentConfig.due_day || 1;
    const today = new Date();
    let dueDate;
    
    // Calculate next due date
    if (today.getDate() < dueDay) {
      dueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
    } else {
      // Next month
      dueDate = new Date(today.getFullYear(), today.getMonth() + 1, dueDay);
    }
    
    // Add days for late fee
    const lateFeeDate = new Date(dueDate);
    lateFeeDate.setDate(lateFeeDate.getDate() + parseInt(daysAfterDue || 0));
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[lateFeeDate.getMonth()]} ${lateFeeDate.getDate()}, ${lateFeeDate.getFullYear()}`;
  };
  
  // Helper function to calculate late fee amount preview
  const calculateLateFeeAmount = (feeType, feeAmount) => {
    if (!paymentConfig) return null;
    
    const duesAmount = paymentConfig.dues_amount || 0;
    
    if (feeType === 'percentage') {
      const amount = (duesAmount * (parseFloat(feeAmount) || 0)) / 100;
      return `$${duesAmount.toFixed(2)} × ${feeAmount}% = $${amount.toFixed(2)}`;
    } else {
      return `$${parseFloat(feeAmount || 0).toFixed(2)} flat fee`;
    }
  };
  
  // Helper function to calculate interest amount preview
  const calculateInterestAmount = (ratePercentage, accrual) => {
    if (!paymentConfig) return null;
    
    const duesAmount = paymentConfig.dues_amount || 0;
    const rate = parseFloat(ratePercentage) || 0;
    const amount = (duesAmount * rate) / 100;
    
    const period = accrual === 'daily' ? 'day' : accrual === 'monthly' ? 'month' : 'year';
    return `$${duesAmount.toFixed(2)} × ${rate}% = $${amount.toFixed(2)}/${period}`;
  };

  useEffect(() => {
    loadProperties();
    if (isBoard) {
      loadConfig();
      setViewMode('all'); // Board starts with all properties view
    } else {
      setViewMode('single'); // Homeowners only see single view
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'all' && properties.length > 0 && isBoard) {
      loadAllLedgers();
    }
  }, [viewMode, properties]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/ledger/${user.hoa_id}/properties`);
      const props = response.data.properties || [];
      console.log('Ledger properties loaded:', props.map(p => ({
        address: p.property_address,
        mailing: p.mailing_address,
        identifier: p.property_identifier
      })));
      setProperties(props);
      
      // Auto-select first property (or user's property for homeowners)
      if (props.length > 0) {
        if (!isBoard && user.property_address) {
          const userProp = props.find(p => p.property_address === user.property_address);
          if (userProp) {
            setSelectedProperty(userProp);
            loadLedger(userProp.property_address);
          }
        } else {
          setSelectedProperty(props[0]);
          loadLedger(props[0].property_address);
        }
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await api.get(`/api/ledger/${user.hoa_id}/config`);
      setConfig(response.data.config);
      setEditingConfig(response.data.config);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await api.put(`/api/ledger/${user.hoa_id}/config`, editingConfig);
      setConfig(editingConfig);
      setShowConfigModal(false);
      alert('✅ Configuration saved successfully!');
      loadConfig(); // Reload to refresh display
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDeletion.length === 0) {
      alert('⚠️ Please select accounts to delete (checkboxes next to accounts with no email)');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ Delete ${selectedForDeletion.length} account(s) without users?\n\nThis will delete:\n- The account\n- All ledger entries\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // selectedForDeletion contains ledger_property_ids from accounts without emails
      const { data } = await api.post(`/api/ledger/${user.hoa_id}/delete-manual-accounts`, selectedForDeletion);
      
      alert(
        `✅ Deleted ${data.deleted_count} account(s)!\n` +
        `${data.errors.length > 0 ? `\nErrors:\n${data.errors.join('\n')}` : ''}`
      );
      
      setSelectedForDeletion([]);
      loadProperties();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('❌ Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddProperty = async () => {
    if (!newProperty.property_address) {
      alert('Property address is required');
      return;
    }

    try {
      // Filter out empty names before sending
      const propertyData = {
        ...newProperty,
        account_holder_names: newProperty.account_holder_names.filter(name => name.trim() !== '')
      };
      await api.post(`/api/ledger/${user.hoa_id}/add-manual-property`, propertyData);
      alert('✅ Property added successfully!');
      setShowAddPropertyModal(false);
      setNewProperty({
        property_address: '',
        property_identifier: '',
        account_holder_names: ['', '', ''],
        notes: ''
      });
      loadProperties();
    } catch (error) {
      console.error('Error adding property:', error);
      alert('Failed to add property: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete) return;

    try {
      const response = await api.delete(
        `/api/ledger/${user.hoa_id}/delete-manual-property/${encodeURIComponent(propertyToDelete.property_address)}`
      );
      
      alert(`✅ ${response.data.message}\n\nLedger entries archived: ${response.data.ledger_entries_archived}`);
      setShowDeletePropertyModal(false);
      setPropertyToDelete(null);
      loadProperties();
      loadAllLedgers();
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('❌ Failed to delete property: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetFees = async (propertyAddress) => {
    if (!window.confirm('⚠️ Reset all late fees and interest for this property?\n\nThis will remove ALL late fee and interest charges. This action cannot be undone.\n\nIf you are reducing fees due to a payment, use Manual Payments instead.')) {
      return;
    }

    try {
      const response = await api.post(`/api/ledger/${user.hoa_id}/reset-fees/${encodeURIComponent(propertyAddress)}`);
      alert(response.data.message);
      loadLedger(propertyAddress);
    } catch (error) {
      console.error('Error resetting fees:', error);
      alert('Failed to reset fees: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadLedger = async (propertyAddress) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/ledger/${user.hoa_id}/ledger/${encodeURIComponent(propertyAddress)}`);
      setLedger(response.data.ledger);
    } catch (error) {
      console.error('Error loading ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = async (propLedger) => {
    // If clicking the same property, collapse it
    if (expandedProperty === propLedger.property_address) {
      setExpandedProperty(null);
      setExpandedLedger(null);
      return;
    }
    
    // Load detailed ledger for this property
    setLoadingExpanded(true);
    try {
      const response = await api.get(
        `/api/ledger/${user.hoa_id}/ledger/${encodeURIComponent(propLedger.property_address)}`
      );
      setExpandedProperty(propLedger.property_address);
      setExpandedLedger(response.data.ledger);
    } catch (error) {
      console.error('Failed to load property ledger detail', error);
      alert('Could not load detail for this property');
    } finally {
      setLoadingExpanded(false);
    }
  };

  const loadAllLedgers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/ledger/${user.hoa_id}/all-ledgers`);
      const ledgers = response.data.ledgers || [];
      console.log('All ledgers loaded:', ledgers.length, 'properties');
      console.log('First 3 ledgers:', ledgers.slice(0, 3).map(l => ({
        address: l.property_address,
        emails: l.emails,
        ledger_property_id: l.ledger_property_id,
        hasEmails: l.emails && l.emails.length > 0
      })));
      setAllLedgers(ledgers);
    } catch (error) {
      console.error('Error loading all ledgers:', error);
      // Fallback to old method if new endpoint fails
      try {
        const ledgerPromises = properties.map(prop =>
          api.get(`/api/ledger/${user.hoa_id}/ledger/${encodeURIComponent(prop.property_address)}`)
            .then(res => ({
              ...prop,
              ...res.data.ledger
            }))
            .catch(err => ({
              ...prop,
              error: true,
              balance_due: 0
            }))
        );
        
        const ledgers = await Promise.all(ledgerPromises);
        setAllLedgers(ledgers);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    loadLedger(property.property_address);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatLocal(date, hoaTimezone, 'MMM d, yyyy');
  };

  const handleExportCSV = async (propertyAddress = null, type = 'summary') => {
    try {
      const params = {
        export_type: type
      };
      if (propertyAddress) {
        params.property_address = propertyAddress;
      }
      
      const response = await api.get(`/api/ledger/${user.hoa_id}/export/csv`, {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const typeLabel = type === 'detailed' ? 'detailed' : 'summary';
      link.download = `ledger_${typeLabel}_${propertyAddress || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`✅ CSV exported successfully!\n\nType: ${type === 'detailed' ? 'Transaction Details' : 'Property Summary'}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExportExcel = async (propertyAddress = null) => {
    try {
      const params = propertyAddress ? { property_address: propertyAddress } : {};
      
      const response = await api.get(`/api/ledger/${user.hoa_id}/export/excel`, {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ledger_${propertyAddress || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleImportCSV = async (event, type = 'summary') => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Step 1: Validate the CSV first
      const formData = new FormData();
      formData.append('file', file);

      const validationResponse = await api.post(`/api/ledger/${user.hoa_id}/import/csv/validate?import_type=${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportValidation({...validationResponse.data, import_type: type});
      
      // Always show confirmation modal to preview changes
      if (validationResponse.data.has_mismatches || validationResponse.data.has_errors || validationResponse.data.to_add > 0 || validationResponse.data.to_update > 0 || validationResponse.data.to_delete > 0) {
        setPendingImportFile(file);
        setShowImportConfirmModal(true);
      } else {
        // Empty file or no changes
        alert('No changes to import');
      }
      
      // Clear file input
      event.target.value = '';
    } catch (error) {
      console.error('Error validating CSV:', error);
      alert('❌ Failed to validate CSV: ' + (error.response?.data?.detail || error.message));
      event.target.value = '';
    }
  };

  const proceedWithImport = async (file, type = 'summary', force = false) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/ledger/${user.hoa_id}/import/csv?import_type=${type}${force ? '&force=true' : ''}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (type === 'detailed') {
        alert(`✅ CSV imported successfully!\n\nAdded: ${response.data.added_count} entries\nUpdated: ${response.data.updated_count} entries\nDeleted: ${response.data.deleted_count || 0} entries\nSkipped: ${response.data.skipped_count || 0} entries\nErrors: ${response.data.errors?.length || 0}`);
      } else {
        alert(`✅ CSV imported successfully!\n\nUpdated: ${response.data.updated_count} properties\nDeleted: ${response.data.deleted_count || 0} properties\nSkipped: ${response.data.skipped_count || 0} properties\nErrors: ${response.data.errors?.length || 0}`);
      }
      
      // Reload ledger data
      await loadAllLedgers();
      setShowImportConfirmModal(false);
      setPendingImportFile(null);
      setImportValidation(null);
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('❌ Failed to import CSV: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveBalanceEdit = async (propertyAddress, field) => {
    try {
      await api.post(`/api/ledger/${user.hoa_id}/property/${propertyAddress}/update-balance`, {
        field,
        value: parseFloat(balanceEditValue) || 0
      });
      
      setEditingBalance(null);
      setBalanceEditValue('');
      await loadAllLedgers();
    } catch (error) {
      console.error('Error updating balance:', error);
      alert('Failed to update balance: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePreviewStatement = async () => {
    if (statementConfig.selected_properties.length === 0) {
      alert('Please select at least one property to preview');
      return;
    }

    try {
      setLoading(true);
      
      // Call preview endpoint
      const response = await api.post(`/api/ledger/${user.hoa_id}/preview-statement`, {
        statement_type: statementConfig.statement_type,
        property_addresses: statementConfig.selected_properties,  // Uses first selected
        custom_notes: statementConfig.custom_notes,
        invoice_instruction: statementConfig.invoice_instruction,
        email_instruction: statementConfig.email_instruction,
        allow_zero_line_items: statementConfig.allow_zero_line_items  // Include the flag
      }, {
        responseType: 'blob'  // Important: receive PDF as blob
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
    } catch (error) {
      console.error('Error previewing statement:', error);
      alert('Failed to generate preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStatements = async () => {
    if (statementConfig.selected_properties.length === 0) {
      alert('Please select at least one property');
      return;
    }

    try {
      const response = await api.post(`/api/ledger/${user.hoa_id}/generate-statements`, {
        statement_type: statementConfig.statement_type,
        property_addresses: statementConfig.selected_properties,
        custom_notes: statementConfig.custom_notes,
        invoice_instruction: statementConfig.invoice_instruction,
        email_instruction: statementConfig.email_instruction,
        allow_zero_line_items: statementConfig.allow_zero_line_items  // Include the flag
      });
      
      alert(response.data.message + '\n\nStatements have been saved to Documents tab under "Owner Statements" category.');
      setShowGenerateStatementsModal(false);
      setStatementConfig({ 
        statement_type: 'upcoming_dues', 
        selected_properties: [], 
        custom_notes: '',
        invoice_instruction: '',
        email_instruction: '',
        allow_zero_line_items: false
      });
    } catch (error) {
      console.error('Error generating statements:', error);
      alert('Failed to generate statements: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSyncDues = async () => {
    if (!window.confirm('Sync dues from Payment Configuration?\n\nThis will create annual dues entries for all properties based on the payment configuration.')) {
      return;
    }

    try {
      const year = new Date().getFullYear();
      const response = await api.post(`/api/ledger/${user.hoa_id}/sync-dues`, { year });
      alert(response.data.message);
      if (viewMode === 'all') {
        loadAllLedgers();
      } else {
        loadLedger(selectedProperty.property_address);
      }
    } catch (error) {
      console.error('Error syncing dues:', error);
      alert('Failed to sync dues: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveAdditionalName = async (propertyAddress) => {
    try {
      await api.post(`/api/ledger/${user.hoa_id}/property/${encodeURIComponent(propertyAddress)}/additional-name`, {
        additional_name: additionalNameValue
      });
      
      // Update local state
      setAllLedgers(allLedgers.map(l => 
        l.property_address === propertyAddress 
          ? {...l, additional_name: additionalNameValue}
          : l
      ));
      
      setEditingAdditionalName(null);
      setAdditionalNameValue('');
    } catch (error) {
      console.error('Error saving additional name:', error);
      alert('Failed to save additional name');
    }
  };

  const handleSaveMailingAddress = async (propertyAddress) => {
    try {
      await api.post(`/api/ledger/${user.hoa_id}/property/${encodeURIComponent(propertyAddress)}/mailing-address`, {
        mailing_address: mailingAddressValue
      });
      
      // Update local state
      setAllLedgers(allLedgers.map(l => 
        l.property_address === propertyAddress 
          ? {...l, mailing_address: mailingAddressValue}
          : l
      ));
      // Also update selectedProperty if it matches
      if (selectedProperty?.property_address === propertyAddress) {
        setSelectedProperty({...selectedProperty, mailing_address: mailingAddressValue});
      }
      setEditingMailingAddress(null);
      setMailingAddressValue('');
    } catch (error) {
      console.error('Error saving mailing address:', error);
      alert('Failed to save mailing address');
    }
  };

  const handleSaveNotes = async (propertyAddress) => {
    try {
      await api.post(`/api/ledger/${user.hoa_id}/property/${encodeURIComponent(propertyAddress)}/notes`, {
        notes: notesValue
      });
      
      // Update local state
      setAllLedgers(allLedgers.map(l => 
        l.property_address === propertyAddress 
          ? {...l, notes: notesValue}
          : l
      ));
      
      setEditingNotes(null);
      setNotesValue('');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    }
  };

  const handleSavePropertyIdentifier = async (propertyAddress) => {
    try {
      await api.post(`/api/ledger/${user.hoa_id}/property/${encodeURIComponent(propertyAddress)}/property-identifier`, {
        property_identifier: propertyIdentifierValue
      });
      
      // Update local state
      setAllLedgers(allLedgers.map(l => 
        l.property_address === propertyAddress 
          ? {...l, property_identifier: propertyIdentifierValue}
          : l
      ));
      
      // Also update selectedProperty if it matches
      if (selectedProperty?.property_address === propertyAddress) {
        setSelectedProperty({...selectedProperty, property_identifier: propertyIdentifierValue});
      }
      
      setEditingPropertyIdentifier(null);
      setPropertyIdentifierValue('');
    } catch (error) {
      console.error('Error saving property identifier:', error);
      alert('Failed to save property identifier');
    }
  };

  const handleImportExisting = async () => {
    if (!window.confirm('Import existing payments?\n\nThis will import all payment transactions from Payment Management into the ledger.')) {
      return;
    }

    try {
      const response = await api.post(`/api/ledger/${user.hoa_id}/import-existing-payments`);
      alert(response.data.message);
      if (viewMode === 'all') {
        loadAllLedgers();
      } else {
        loadLedger(selectedProperty.property_address);
      }
    } catch (error) {
      console.error('Error importing:', error);
      alert('Failed to import: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCalculateFees = async () => {
    if (!window.confirm('Calculate late fees and interest?\n\nThis will apply fees based on configured rules for all overdue items.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/api/ledger/${user.hoa_id}/calculate-fees`);
      alert(response.data.message || 'Fees calculated successfully');
      loadAllLedgers();
    } catch (error) {
      console.error('Error calculating fees:', error);
      alert('Failed to calculate fees: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOverrideAmount = async (entry) => {
    const newAmount = prompt(`Override amount for "${entry.description}"?\n\nCurrent: $${entry.amount}\nEnter new amount:`, entry.amount);
    if (newAmount === null) return;
    
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount < 0) {
      alert('Invalid amount');
      return;
    }

    try {
      const response = await api.post(`/api/ledger/${user.hoa_id}/override-amount`, {
        entry_id: entry.id,
        new_amount: amount
      });
      
      if (response.data.warning) {
        if (window.confirm(response.data.message)) {
          // User confirmed, call again with confirmation
          await api.post(`/api/ledger/${user.hoa_id}/override-amount`, {
            entry_id: entry.id,
            new_amount: amount,
            confirmed: true
          });
          alert('Amount overridden successfully');
        } else {
          return;
        }
      } else {
        alert(response.data.message || 'Amount updated');
      }
      
      if (viewMode === 'all') {
        loadAllLedgers();
      } else {
        loadLedger(selectedProperty.property_address);
      }
    } catch (error) {
      console.error('Error overriding amount:', error);
      alert('Failed to override: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddBalanceForward = async () => {
    if (!balanceForwardData.property_address || !balanceForwardData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await api.post(`/api/ledger/${user.hoa_id}/add-charge`, {
        property_address: balanceForwardData.property_address,
        amount: parseFloat(balanceForwardData.amount),
        transaction_type: 'balance_forward',
        description: balanceForwardData.description,
        due_date: new Date(balanceForwardData.due_date).toISOString()
      });
      
      alert('Balance forward added successfully');
      setShowBalanceForwardModal(false);
      setBalanceForwardData({
        property_address: '',
        amount: '',
        description: 'Balance Forward',
        due_date: new Date().toISOString().split('T')[0]
      });
      
      if (viewMode === 'all') {
        loadAllLedgers();
      } else {
        loadLedger(selectedProperty.property_address);
      }
    } catch (error) {
      console.error('Error adding balance forward:', error);
      alert('Failed to add: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Property Ledger</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track assessments, payments, late fees, and interest
          </p>
        </div>
        {isBoard && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAddPropertyModal(true)}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 whitespace-nowrap"
            >
              ➕ Add Property
            </button>
            <button
              onClick={handleImportExisting}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 whitespace-nowrap"
            >
              📥 Import Existing
            </button>
            <button
              onClick={handleSyncDues}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap"
            >
              🔄 Sync Dues
            </button>
            <button
              onClick={handleCalculateFees}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap"
            >
              🧮 Calculate Fees
            </button>
            <button
              onClick={() => setShowBalanceForwardModal(true)}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 whitespace-nowrap"
            >
              💰 Balance Forward
            </button>
            <button
              onClick={async () => {
                setCheckingMerges(true);
                try {
                  const { data } = await api.post('/api/ledger/check-and-merge');
                  if (data.merges_performed && data.merges_performed.length > 0) {
                    alert(`✅ Merged ${data.merges_performed.length} manual ledger account(s) successfully!\n\n${data.merges_performed.map(m => m.manual_account_name).join('\n')}`);
                    // Reload ledgers
                    loadAllLedgers();
                  } else {
                    alert('ℹ️ No manual ledger accounts found that need merging.');
                  }
                } catch (error) {
                  console.error('Error checking merges:', error);
                  alert('❌ Failed to check for ledger merges. Please try again.');
                } finally {
                  setCheckingMerges(false);
                }
              }}
              disabled={checkingMerges}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-orange-600 text-white rounded-lg hover:bg-orange-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkingMerges ? '🔄 Checking...' : '🔀 Merge Manual Accounts'}
            </button>
            
            {/* Delete Property with No Users */}
            <button
              onClick={async () => {
                const propertyAddress = prompt('Enter property address to delete (only works for properties with no users):');
                
                if (!propertyAddress) return;
                
                // Check if property exists and has users
                const property = allLedgers.find(p => p.property_address === propertyAddress);
                
                if (!property) {
                  alert(`Property "${propertyAddress}" not found in ledger.`);
                  return;
                }
                
                // Check if property has users
                if (property.emails && property.emails.length > 0) {
                  alert(`❌ Cannot delete "${propertyAddress}"\n\nThis property has active users:\n${property.emails.join(', ')}\n\nRemove users first.`);
                  return;
                }
                
                if (!confirm(`⚠️ DELETE: ${propertyAddress}\n\nRemoves all transactions, manual accounts, summary.\n\nCONTINUE?`)) {
                  return;
                }
                
                try {
                  const response = await api.post('/api/user-scanner/ledger/delete-safe', null, {
                    params: { hoa_id: user.hoa_id, property_address: propertyAddress }
                  });
                  
                  alert(`✅ DELETED: ${propertyAddress}\n\n${response.data.results.actions_taken.join('\n')}`);
                  loadAllLedgers();
                } catch (error) {
                  const errorMsg = error.response?.data?.detail || error.message;
                  alert(`❌ Delete failed: ${errorMsg}`);
                }
              }}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-red-600 text-white rounded-lg hover:bg-red-700 whitespace-nowrap"
            >
              🗑️ Delete Property (No User)
            </button>
            <button
              data-testid="reset-balances-btn"
              onClick={() => { setShowResetModal(true); loadResetBackups(); }}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-red-700 text-white rounded-lg hover:bg-red-800 whitespace-nowrap"
            >
              Reset Balances
            </button>
            {/* Manual Merge - Commented out since ledger_property_id system prevents duplicates
            <button
              onClick={async () => {
                console.log('[Manual Merge] Button clicked');
                setCheckingMerges(true);
                try {
                  // Get all properties
                  console.log('[Manual Merge] Fetching properties for HOA:', user.hoa_id);
                  const { data } = await api.get(`/api/ledger/${user.hoa_id}/all-properties-with-entries`);
                  console.log('[Manual Merge] Received data:', data);
                  console.log('[Manual Merge] Properties count:', data.properties?.length || 0);
                  setAllLedgerProperties(data.properties || []);
                  setShowManualMergeModal(true);
                  console.log('[Manual Merge] Modal should now be visible');
                } catch (error) {
                  console.error('[Manual Merge] Error:', error);
                  alert('❌ Failed to load properties: ' + (error.response?.data?.detail || error.message));
                } finally {
                  setCheckingMerges(false);
                }
              }}
              disabled={checkingMerges}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkingMerges ? '🔄 Loading...' : '🔀 Manual Merge'}
            </button>
            */}
            <button
              onClick={async () => {
                setShowConfigModal(true);
                // Fetch payment config for preview calculations
                try {
                  const { data } = await api.get('/api/payment/configuration');
                  setPaymentConfig(data.configuration || data);
                  console.log('Payment config loaded:', data.configuration || data);
                } catch (err) {
                  console.error('Failed to fetch payment config:', err);
                }
              }}
              className="px-3 py-2 text-sm md:px-4 md:py-2 md:text-base bg-gray-600 text-white rounded-lg hover:bg-gray-700 whitespace-nowrap"
            >
              ⚙️ Configure Rules
            </button>
          </div>
        )}
      </div>

      {/* Current Rules Display */}
      {isBoard && config && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Current Late Fee & Interest Rules</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Late Fee Rules:</h4>
              {config.late_fee_rules && config.late_fee_rules.length > 0 ? (
                <div className="space-y-2">
                  {config.late_fee_rules.map((rule, idx) => (
                    <div key={idx} className="text-sm bg-gray-50 p-3 rounded border">
                      <p><strong>{rule.activity_type}:</strong> {rule.enabled ? '✓ Active' : '✗ Disabled'}</p>
                      <p>After {rule.days_after_due} days → {rule.fee_amount}{rule.fee_type === 'percentage' ? '%' : '$'} ({rule.accrual})</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No late fee rules configured</p>
              )}
            </div>
            <div>
              <h4 className="font-medium mb-2">Interest Rules:</h4>
              {config.interest_rules && config.interest_rules.length > 0 ? (
                <div className="space-y-2">
                  {config.interest_rules.map((rule, idx) => (
                    <div key={idx} className="text-sm bg-gray-50 p-3 rounded border">
                      <p><strong>{rule.activity_type}:</strong> {rule.enabled ? '✓ Active' : '✗ Disabled'}</p>
                      <p>After {rule.days_after_due} days → {rule.rate_percentage}% ({rule.accrual})</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No interest rules configured</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Property Selector */}
      {isBoard && properties.length > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">View Mode:</label>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('all')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  viewMode === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📊 All Properties
              </button>
              <button
                onClick={() => setViewMode('single')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  viewMode === 'single'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🏠 Single Property
              </button>
            </div>
          </div>
          
          {viewMode === 'single' && (
            <div className="mt-3">
              <label className="block text-sm font-medium mb-2">Select Property</label>
              <select
                onChange={(e) => {
                  const prop = properties.find(p => p.property_address === e.target.value);
                  if (prop) handlePropertySelect(prop);
                }}
                value={selectedProperty?.property_address || ''}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {properties.map(prop => (
                  <option key={prop.property_address} value={prop.property_address}>
                    {prop.property_address} - {prop.account_holder_names.join(', ') || 'No name'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Ledger Summary */}
      {viewMode === 'all' && isBoard && allLedgers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
            <h3 className="text-xl font-semibold">All Properties Overview</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowExportModal(true)}
                className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 whitespace-nowrap"
              >
                📥 Export CSV
              </button>
              <button
                onClick={() => document.getElementById('csvImportDetailed').click()}
                className="px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 whitespace-nowrap"
              >
                📤 Import CSV
              </button>
              <input
                id="csvImportDetailed"
                type="file"
                accept=".csv"
                style={{display: 'none'}}
                onChange={(e) => handleImportCSV(e, 'detailed')}
              />
              {selectedForDeletion.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 whitespace-nowrap"
                >
                  🗑️ Delete Selected ({selectedForDeletion.length})
                </button>
              )}
              <button
                onClick={() => setShowGenerateStatementsModal(true)}
                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 whitespace-nowrap"
              >
                📄 Generate Statements
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="font-semibold mb-2">💡 Editing Tips:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li><strong>Click ▸</strong> next to a property to expand and view transaction details</li>
              <li><strong>Edit, Add, or Delete</strong> line items directly within the expanded view</li>
              <li><strong>Balances auto-calculate</strong> from transaction line items for accuracy</li>
              <li><strong>Green values</strong> indicate credits (negative balances)</li>
            </ul>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2">
                <tr>
                  <th className="px-4 py-3 text-left">Property</th>
                  <th className="px-4 py-3 text-left">Mailing Address</th>
                  <th className="px-4 py-3 text-left">Lot #</th>
                  <th className="px-4 py-3 text-left">Account 1</th>
                  <th className="px-4 py-3 text-left">Account 2</th>
                  <th className="px-4 py-3 text-left">Additional Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Notes</th>
                  <th className="px-4 py-3 text-right">Balance Forward</th>
                  <th className="px-4 py-3 text-right">Dues</th>
                  <th className="px-4 py-3 text-right">Late Fees</th>
                  <th className="px-4 py-3 text-right">Interest</th>
                  <th className="px-4 py-3 text-right">Fines</th>
                  <th className="px-4 py-3 text-right">Events</th>
                  <th className="px-4 py-3 text-right">Other</th>
                  <th className="px-4 py-3 text-right">Balance Due</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allLedgers
                  .sort((a, b) => (b.balance_due || 0) - (a.balance_due || 0))
                  .map((propLedger, idx) => {
                    // Debug log first 3 properties
                    if (idx < 3) {
                      console.log(`Ledger ${idx}:`, {
                        address: propLedger.property_address,
                        emails: propLedger.emails,
                        ledger_property_id: propLedger.ledger_property_id,
                        hasEmails: propLedger.emails && propLedger.emails.length > 0
                      });
                    }
                    return (
                  <React.Fragment key={propLedger.property_address}>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        {/* Checkbox for accounts without real user email - these are deletable */}
                        {propLedger.ledger_property_id && (!propLedger.emails || propLedger.emails.length === 0) && (
                          <input
                            type="checkbox"
                            checked={selectedForDeletion.includes(propLedger.ledger_property_id)}
                            onChange={(e) => {
                              console.log('Checkbox clicked for:', propLedger.property_address, 'ID:', propLedger.ledger_property_id);
                              if (e.target.checked) {
                                setSelectedForDeletion([...selectedForDeletion, propLedger.ledger_property_id]);
                              } else {
                                setSelectedForDeletion(selectedForDeletion.filter(id => id !== propLedger.ledger_property_id));
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="mr-1 w-4 h-4"
                            title="Select for deletion"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleExpand(propLedger)}
                          className="text-gray-500 hover:text-gray-700 text-sm font-bold w-5"
                          title={expandedProperty === propLedger.property_address ? "Hide transactions" : "Show transactions"}
                        >
                          {expandedProperty === propLedger.property_address ? '▾' : '▸'}
                        </button>
                        <span>{propLedger.property_address}</span>
                        {(!propLedger.emails || propLedger.emails.length === 0) && (
                          <span className="text-xs text-red-500 ml-2">(no user)</span>
                        )}
                      </div>
                    </td>
                    {/* Mailing Address - Editable */}
                    <td className="px-4 py-3">
                      {editingMailingAddress === propLedger.property_address ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={mailingAddressValue}
                            onChange={(e) => setMailingAddressValue(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-40"
                            autoFocus
                            placeholder="PO Box..."
                          />
                          <button
                            onClick={() => handleSaveMailingAddress(propLedger.property_address)}
                            className="text-green-600 hover:text-green-700 text-xs"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingMailingAddress(null);
                              setMailingAddressValue('');
                            }}
                            className="text-red-600 hover:text-red-700 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm"
                          onClick={() => {
                            setEditingMailingAddress(propLedger.property_address);
                            setMailingAddressValue(propLedger.mailing_address || '');
                          }}
                        >
                          {propLedger.mailing_address || <span className="text-gray-400 text-xs">Click to add</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingPropertyIdentifier === propLedger.property_address ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={propertyIdentifierValue}
                            onChange={(e) => setPropertyIdentifierValue(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-32"
                            placeholder="Lot #"
                          />
                          <button
                            onClick={() => handleSavePropertyIdentifier(propLedger.property_address)}
                            className="text-green-600 hover:text-green-700 text-xs"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingPropertyIdentifier(null);
                              setPropertyIdentifierValue('');
                            }}
                            className="text-red-600 hover:text-red-700 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-sm"
                          onClick={() => {
                            setEditingPropertyIdentifier(propLedger.property_address);
                            setPropertyIdentifierValue(propLedger.property_identifier || propLedger.lot_number || '');
                          }}
                        >
                          {propLedger.property_identifier || propLedger.lot_number || <span className="text-gray-400 italic">Click to add</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">{propLedger.account_holder_names?.[0] || '-'}</td>
                    <td className="px-4 py-3">{propLedger.account_holder_names?.[1] || '-'}</td>
                    <td className="px-4 py-3">
                      {editingAdditionalName === propLedger.property_address ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={additionalNameValue}
                            onChange={(e) => setAdditionalNameValue(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-32"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveAdditionalName(propLedger.property_address)}
                            className="text-green-600 hover:text-green-700 text-xs"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingAdditionalName(null);
                              setAdditionalNameValue('');
                            }}
                            className="text-red-600 hover:text-red-700 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                          onClick={() => {
                            setEditingAdditionalName(propLedger.property_address);
                            setAdditionalNameValue(propLedger.additional_name || '');
                          }}
                        >
                          {propLedger.additional_name || <span className="text-gray-400 text-xs">Click to add</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {propLedger.emails && propLedger.emails.length > 0 ? (
                        <div className="space-y-1">
                          {propLedger.emails.map((email, idx) => (
                            <a key={idx} href={`mailto:${email}`} className="text-blue-600 hover:underline block">
                              {email}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No email</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingNotes === propLedger.property_address ? (
                        <div className="flex items-center gap-2">
                          <textarea
                            value={notesValue}
                            onChange={(e) => setNotesValue(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-48"
                            rows="2"
                            autoFocus
                          />
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleSaveNotes(propLedger.property_address)}
                              className="text-green-600 hover:text-green-700 text-xs"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setEditingNotes(null);
                                setNotesValue('');
                              }}
                              className="text-red-600 hover:text-red-700 text-xs"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded max-w-xs"
                          onClick={() => {
                            setEditingNotes(propLedger.property_address);
                            setNotesValue(propLedger.notes || '');
                          }}
                        >
                          {propLedger.notes ? (
                            <span className="text-sm whitespace-pre-wrap">{propLedger.notes}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Click to add notes</span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Balance Forward - Auto-calculated from breakout */}
                    <td className="px-4 py-3 text-right">
                      <div className="px-1 text-blue-600">
                        {formatCurrency(propLedger.balance_forward || 0)}
                      </div>
                    </td>
                    {/* Current Dues - Auto-calculated from breakout */}
                    <td className="px-4 py-3 text-right">
                      <div className="px-1">
                        {formatCurrency(propLedger.current_dues || 0)}
                      </div>
                    </td>
                    {/* Late Fees - Auto-calculated from breakout */}
                    <td className="px-4 py-3 text-right text-red-600">
                      <div className="px-1">
                        {formatCurrency(propLedger.late_fees || 0)}
                      </div>
                    </td>
                    {/* Interest - Auto-calculated from breakout */}
                    <td className="px-4 py-3 text-right text-purple-600">
                      <div className="px-1">
                        {formatCurrency(propLedger.interest || 0)}
                      </div>
                    </td>
                    {/* Fines - Auto-calculated from breakout */}
                    <td className="px-4 py-3 text-right text-yellow-600">
                      <div className="px-1">
                        {formatCurrency(propLedger.fines || 0)}
                      </div>
                    </td>
                    {/* Events (read-only for now, can be made editable like other fields) */}
                    <td className="px-4 py-3 text-right text-indigo-600">
                      <div className="px-1">
                        {formatCurrency(propLedger.events || 0)}
                      </div>
                    </td>
                    {/* Other (read-only for now, can be made editable like other fields) */}
                    <td className="px-4 py-3 text-right text-gray-600">
                      <div className="px-1">
                        {formatCurrency(propLedger.other || 0)}
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      (propLedger.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(propLedger.balance_due || 0)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => {
                            setViewMode('single');
                            handlePropertySelect(propLedger);
                          }}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          View Details
                        </button>
                        {propLedger.is_manual && (
                          <button
                            onClick={() => {
                              setPropertyToDelete(propLedger);
                              setShowDeletePropertyModal(true);
                            }}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            title="Delete this manually added property"
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expandable detail row */}
                  {expandedProperty === propLedger.property_address && (
                    <tr className="bg-gray-50">
                      <td colSpan={14} className="px-4 py-4">
                        {loadingExpanded ? (
                          <div className="text-center text-sm text-gray-500 py-4">
                            Loading transaction details...
                          </div>
                        ) : expandedLedger ? (
                          <PropertyTransactionMiniTable
                            ledger={expandedLedger}
                            property_address={propLedger.property_address}
                            isBoard={isBoard}
                            user={user}
                            onLedgerChanged={() => {
                              loadAllLedgers();
                              handleToggleExpand(propLedger); // Refresh detail
                            }}
                            showConsolidationModal={showConsolidationModal}
                            setShowConsolidationModal={setShowConsolidationModal}
                            consolidationPreview={consolidationPreview}
                            setConsolidationPreview={setConsolidationPreview}
                            selectedForConsolidation={selectedForConsolidation}
                            setSelectedForConsolidation={setSelectedForConsolidation}
                            checkingMerges={checkingMerges}
                            setCheckingMerges={setCheckingMerges}
                            showManualMergeModal={showManualMergeModal}
                            setShowManualMergeModal={setShowManualMergeModal}
                            loadAllLedgers={loadAllLedgers}
                          />
                        ) : (
                          <div className="text-center text-sm text-gray-500 py-4">
                            No transaction data available
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                )})}
              </tbody>
              <tfoot className="bg-gray-50 font-bold">
                <tr>
                  <td colSpan="8" className="px-4 py-3">TOTALS</td>
                  <td className="px-4 py-3 text-right text-blue-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.balance_forward || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.current_dues || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-red-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.late_fees || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-purple-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.interest || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-yellow-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.fines || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-indigo-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.events || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.other || 0), 0))}
                  </td>
                  <td className={`px-4 py-3 text-right ${
                    allLedgers.reduce((sum, l) => sum + (l.balance_due || 0), 0) > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {formatCurrency(allLedgers.reduce((sum, l) => sum + (l.balance_due || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {ledger && viewMode === 'single' && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-semibold">{ledger.property_address}</h3>
              {selectedProperty?.account_holder_names && selectedProperty.account_holder_names.length > 0 && (
                <p className="text-gray-600">{selectedProperty.account_holder_names.join(', ')}</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                {/* Property Identifier */}
                {selectedProperty?.property_identifier && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">Lot:</span> {selectedProperty.property_identifier}
                  </div>
                )}
                {/* Mailing Address - Editable - Next to property identifier */}
                <div className="flex items-center">
                  {editingMailingAddress === ledger.property_address ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">📬</span>
                      <input
                        type="text"
                        value={mailingAddressValue}
                        onChange={(e) => setMailingAddressValue(e.target.value)}
                        className="px-2 py-1 border rounded text-xs"
                        placeholder="Mailing address"
                        style={{width: '200px'}}
                      />
                      <button
                        onClick={() => handleSaveMailingAddress(ledger.property_address)}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingMailingAddress(null);
                          setMailingAddressValue('');
                        }}
                        className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div 
                      className={`text-xs ${isBoard ? 'cursor-pointer hover:underline' : ''}`}
                      onClick={() => {
                        if (isBoard) {
                          setEditingMailingAddress(ledger.property_address);
                          setMailingAddressValue(selectedProperty?.mailing_address || '');
                        }
                      }}
                    >
                      {selectedProperty?.mailing_address ? (
                        <span className="text-blue-600">📬 <span className="font-medium">Mail:</span> {selectedProperty.mailing_address}</span>
                      ) : (
                        isBoard ? <span className="text-gray-400">📬 Click to add mailing address</span> : null
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <div className="text-right">
                <div className="text-sm text-gray-600">Balance Due</div>
                <div className={`text-2xl font-bold ${ledger.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(ledger.balance_due)}
                </div>
              </div>
              {isBoard && (ledger.late_fees > 0 || ledger.interest > 0) && (
                <button
                  onClick={() => handleResetFees(ledger.property_address)}
                  className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                >
                  🔄 Reset Late Fees & Interest
                </button>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xs text-blue-600 mb-1">Balance Forward</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.balance_forward)}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-xs text-green-600 mb-1">Current Dues</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.current_dues)}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-xs text-red-600 mb-1">Late Fees</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.late_fees)}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-xs text-purple-600 mb-1">Interest</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.interest)}</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-xs text-yellow-600 mb-1">Fines</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.fines)}</div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="text-xs text-indigo-600 mb-1">Events</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.events)}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Other</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.other)}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-xs text-green-600 mb-1">Total Paid</div>
              <div className="text-lg font-semibold">{formatCurrency(ledger.total_paid)}</div>
            </div>
          </div>

          {/* Transactions Table */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Transaction History</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExportCSV(ledger.property_address)}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => handleExportExcel(ledger.property_address)}
                  className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                >
                  Export Excel
                </button>
              </div>
            </div>

            {ledger.entries && ledger.entries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-center">Status</th>
                      <th className="px-4 py-2 text-left">Paid Date</th>
                      {isBoard && <th className="px-4 py-2 text-center">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map(entry => (
                      <tr key={entry.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">{formatDate(entry.due_date)}</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-1 text-xs rounded bg-gray-100">
                            {entry.transaction_type}
                          </span>
                        </td>
                        <td className="px-4 py-2">{entry.description}</td>
                        <td className={`px-4 py-2 text-right font-medium ${
                          entry.amount > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {entry.paid ? (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Paid</span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">Unpaid</span>
                          )}
                        </td>
                        <td className="px-4 py-2">{formatDate(entry.paid_date)}</td>
                        {isBoard && (
                          <td className="px-4 py-2 text-center">
                            {!entry.paid && (entry.transaction_type === 'late_fee' || entry.transaction_type === 'interest') && (
                              <button
                                onClick={() => handleOverrideAmount(entry)}
                                className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                              >
                                Override
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No transactions yet</p>
            )}
          </div>
        </div>
      )}

      {!ledger && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500">Select a property to view ledger</p>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      )}

      {/* Configure Rules Modal */}
      {showConfigModal && editingConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Configure Late Fee & Interest Rules</h3>
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  setEditingConfig(config);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Late Fee Rules */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Late Fee Rules</h4>
              {editingConfig.late_fee_rules?.map((rule, idx) => (
                <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Activity Type</label>
                      <select
                        value={rule.activity_type}
                        onChange={(e) => {
                          const newRules = [...editingConfig.late_fee_rules];
                          newRules[idx].activity_type = e.target.value;
                          setEditingConfig({...editingConfig, late_fee_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="dues">Dues</option>
                        <option value="fine">Fines</option>
                        <option value="event">Events</option>
                        <option value="balance_forward">Balance Forward</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Days After Due</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={rule.days_after_due}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const newRules = [...editingConfig.late_fee_rules];
                          newRules[idx].days_after_due = val === '' ? 0 : parseInt(val);
                          setEditingConfig({...editingConfig, late_fee_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                      {paymentConfig && (
                        <p className="text-xs text-blue-600 mt-1">
                          📅 Will charge on: {calculateLateFeeDate(rule.days_after_due)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Fee Type</label>
                      <select
                        value={rule.fee_type}
                        onChange={(e) => {
                          const newRules = [...editingConfig.late_fee_rules];
                          newRules[idx].fee_type = e.target.value;
                          setEditingConfig({...editingConfig, late_fee_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="flat">Flat Fee</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {rule.fee_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={rule.fee_amount}
                        onChange={(e) => {
                          const newRules = [...editingConfig.late_fee_rules];
                          newRules[idx].fee_amount = parseFloat(e.target.value);
                          setEditingConfig({...editingConfig, late_fee_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                      {paymentConfig && (
                        <p className="text-xs text-green-600 mt-1">
                          💰 {calculateLateFeeAmount(rule.fee_type, rule.fee_amount)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Accrual</label>
                      <select
                        value={rule.accrual}
                        onChange={(e) => {
                          const newRules = [...editingConfig.late_fee_rules];
                          newRules[idx].accrual = e.target.value;
                          setEditingConfig({...editingConfig, late_fee_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="one_time">One Time Only</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="none">No Late Fee</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Enabled</label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rule.enabled !== false}
                          onChange={(e) => {
                            const newRules = [...editingConfig.late_fee_rules];
                            newRules[idx].enabled = e.target.checked;
                            setEditingConfig({...editingConfig, late_fee_rules: newRules});
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const newRules = editingConfig.late_fee_rules.filter((_, i) => i !== idx);
                      setEditingConfig({...editingConfig, late_fee_rules: newRules});
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    🗑️ Remove Rule
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newRules = [...(editingConfig.late_fee_rules || []), {
                    activity_type: 'dues',
                    enabled: true,
                    days_after_due: 10,
                    fee_type: 'percentage',
                    fee_amount: 5.0,
                    accrual: 'one_time'
                  }];
                  setEditingConfig({...editingConfig, late_fee_rules: newRules});
                }}
                className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                + Add Late Fee Rule
              </button>
            </div>

            {/* Interest Rules */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-3">Interest Rules</h4>
              
              {/* Global Interest Start Setting */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium mb-2">When should interest start accruing?</label>
                <select
                  value={editingConfig.interest_start_timing || 'next_day'}
                  onChange={(e) => setEditingConfig({...editingConfig, interest_start_timing: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="next_day">Immediately after due date (next day)</option>
                  <option value="end_of_month">End of month if still unpaid</option>
                </select>
                <p className="text-xs text-blue-700 mt-2">
                  {editingConfig.interest_start_timing === 'end_of_month' 
                    ? '📌 Interest will be calculated at the end of the month for any unpaid balances'
                    : '📌 Interest will begin accruing the day after the due date'
                  }
                </p>
              </div>
              
              {editingConfig.interest_rules?.map((rule, idx) => (
                <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Activity Type</label>
                      <select
                        value={rule.activity_type}
                        onChange={(e) => {
                          const newRules = [...editingConfig.interest_rules];
                          newRules[idx].activity_type = e.target.value;
                          setEditingConfig({...editingConfig, interest_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="dues">Dues</option>
                        <option value="fine">Fines</option>
                        <option value="event">Events</option>
                        <option value="balance_forward">Balance Forward</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Days After Due</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={rule.days_after_due}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const newRules = [...editingConfig.interest_rules];
                          newRules[idx].days_after_due = val === '' ? 0 : parseInt(val);
                          setEditingConfig({...editingConfig, interest_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                      {paymentConfig && (
                        <p className="text-xs text-blue-600 mt-1">
                          📅 Starts on: {calculateLateFeeDate(rule.days_after_due)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={rule.rate_percentage}
                        onChange={(e) => {
                          const newRules = [...editingConfig.interest_rules];
                          newRules[idx].rate_percentage = parseFloat(e.target.value);
                          setEditingConfig({...editingConfig, interest_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                      {paymentConfig && (
                        <p className="text-xs text-green-600 mt-1">
                          💰 {calculateInterestAmount(rule.rate_percentage, rule.accrual)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Accrual</label>
                      <select
                        value={rule.accrual}
                        onChange={(e) => {
                          const newRules = [...editingConfig.interest_rules];
                          newRules[idx].accrual = e.target.value;
                          setEditingConfig({...editingConfig, interest_rules: newRules});
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="daily">Daily</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                        Compound Interest
                        <span className="group relative">
                          <span className="text-blue-500 cursor-help">ℹ️</span>
                          <div className="hidden group-hover:block absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg -top-2 left-6">
                            <strong>Note:</strong> Interest accrues only on principal balance (dues/fines/events).
                            Once principal is paid, interest stops accruing even if some interest remains unpaid.
                            Interest does NOT accrue on unpaid interest.
                          </div>
                        </span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rule.compound === true}
                          onChange={(e) => {
                            const newRules = [...editingConfig.interest_rules];
                            newRules[idx].compound = e.target.checked;
                            setEditingConfig({...editingConfig, interest_rules: newRules});
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">Compound</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Enabled</label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rule.enabled !== false}
                          onChange={(e) => {
                            const newRules = [...editingConfig.interest_rules];
                            newRules[idx].enabled = e.target.checked;
                            setEditingConfig({...editingConfig, interest_rules: newRules});
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const newRules = editingConfig.interest_rules.filter((_, i) => i !== idx);
                      setEditingConfig({...editingConfig, interest_rules: newRules});
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    🗑️ Remove Rule
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newRules = [...(editingConfig.interest_rules || []), {
                    activity_type: 'dues',
                    enabled: true,
                    days_after_due: 30,
                    rate_percentage: 1.5,
                    accrual: 'monthly',
                    compound: false
                  }];
                  setEditingConfig({...editingConfig, interest_rules: newRules});
                }}
                className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                + Add Interest Rule
              </button>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  setEditingConfig(config);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Statements Modal */}
      {showGenerateStatementsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Generate Statements</h3>
              <button
                onClick={() => setShowGenerateStatementsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Statement Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Statement Type</label>
                <select
                  value={statementConfig.statement_type}
                  onChange={(e) => setStatementConfig({...statementConfig, statement_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="upcoming_dues">📅 Upcoming Dues Notice (Professional)</option>
                  <option value="current_balance">💵 Current Balance Statement</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {statementConfig.statement_type === 'upcoming_dues' 
                    ? 'Includes payment instructions, late fee terms, and registration links'
                    : 'Shows current balance with all outstanding charges'}
                </p>
              </div>

              {/* Standard Note (for all statements) */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Standard Note (Appears on all statements)</label>
                  <button
                    onClick={() => {
                      const defaultInvoice = "TO ENSURE PROPER CREDIT, PLEASE ENCLOSE A COPY OF THIS INVOICE WITH YOUR CHECK OR MONEY ORDER PAYMENT.";
                      const defaultEmail = "The HOA uses HOAmanagerpro.us and you will receive an email receipt, if not enrolled yet provide your email below to activate your account.";
                      setStatementConfig({
                        ...statementConfig,
                        invoice_instruction: defaultInvoice,
                        email_instruction: defaultEmail
                      });
                      alert('Reset to default text');
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Reset to Default
                  </button>
                </div>
                
                {/* Invoice Instruction */}
                <label className="block text-xs text-gray-600 mb-1 mt-2">Line 1 (Invoice Instruction):</label>
                <textarea
                  value={statementConfig.invoice_instruction || "TO ENSURE PROPER CREDIT, PLEASE ENCLOSE A COPY OF THIS INVOICE WITH YOUR CHECK OR MONEY ORDER PAYMENT."}
                  onChange={(e) => setStatementConfig({...statementConfig, invoice_instruction: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows="2"
                  placeholder="Line 1: Invoice enclosure instruction..."
                />
                
                {/* Email Instruction */}
                <label className="block text-xs text-gray-600 mb-1 mt-2">Line 2 (Email Instruction):</label>
                <textarea
                  value={statementConfig.email_instruction || "The HOA uses HOAmanagerpro.us and you will receive an email receipt, if not enrolled yet provide your email below to activate your account."}
                  onChange={(e) => setStatementConfig({...statementConfig, email_instruction: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows="2"
                  placeholder="Line 2: Email receipt instruction..."
                />
                <p className="text-xs text-gray-500 mt-1">These instructions appear on every generated statement</p>
              </div>

              {/* Custom Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">Additional Notes (Optional)</label>
                <textarea
                  value={statementConfig.custom_notes}
                  onChange={(e) => setStatementConfig({...statementConfig, custom_notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Add any important notices or special instructions for homeowners..."
                />
              </div>

              {/* Allow Zero Line Items Checkbox */}
              <div className="border rounded-lg p-3 bg-yellow-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statementConfig.allow_zero_line_items}
                    onChange={(e) => setStatementConfig({
                      ...statementConfig,
                      allow_zero_line_items: e.target.checked
                    })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Generate statement for properties with 0 line items
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Check this box if you want to generate statements for properties that have no ledger entries. 
                      By default, properties with zero line items are skipped.
                    </div>
                  </div>
                </label>
              </div>

              {/* Property Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Select Properties</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatementConfig({
                        ...statementConfig,
                        selected_properties: allLedgers.map(l => l.property_address)
                      })}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-gray-400">|</span>
                    <button
                      onClick={() => setStatementConfig({...statementConfig, selected_properties: []})}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  {allLedgers.map((ledger, idx) => (
                    <label
                      key={idx}
                      className="flex items-center p-3 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={statementConfig.selected_properties.includes(ledger.property_address)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setStatementConfig({
                              ...statementConfig,
                              selected_properties: [...statementConfig.selected_properties, ledger.property_address]
                            });
                          } else {
                            setStatementConfig({
                              ...statementConfig,
                              selected_properties: statementConfig.selected_properties.filter(a => a !== ledger.property_address)
                            });
                          }
                        }}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{ledger.property_address}</div>
                        <div className="text-sm text-gray-600">
                          {ledger.account_holder_names?.join(', ') || 'N/A'} • Balance: {formatCurrency(ledger.balance_due || 0)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="mt-2 text-sm text-gray-600">
                  {statementConfig.selected_properties.length} of {allLedgers.length} properties selected
                </div>
              </div>

              {/* Preview Info */}
              {statementConfig.selected_properties.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Click "Preview Statement" to see exactly how the statement will look before generating {statementConfig.selected_properties.length} statement{statementConfig.selected_properties.length !== 1 ? 's' : ''}.
                    Preview uses the first selected property.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={() => setShowGenerateStatementsModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                
                <div className="flex gap-2">
                  <button
                    onClick={handlePreviewStatement}
                    disabled={loading || statementConfig.selected_properties.length === 0}
                    className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span>👁️</span>
                    {loading ? 'Loading...' : 'Preview Statement'}
                  </button>
                  
                  <button
                    onClick={handleGenerateStatements}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Generate {statementConfig.selected_properties.length} Statement{statementConfig.selected_properties.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Property Confirmation Modal */}
      {showDeletePropertyModal && propertyToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-red-600">⚠️ Delete Property</h3>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-3">
                Are you sure you want to delete this manually added property?
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
                <p className="font-semibold">{propertyToDelete.property_address}</p>
                {propertyToDelete.account_holder_names && propertyToDelete.account_holder_names.length > 0 && (
                  <p className="text-sm text-gray-600">
                    Names: {propertyToDelete.account_holder_names.join(', ')}
                  </p>
                )}
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> This will remove the property from the ledger system. 
                  Any existing ledger entries will be archived (not deleted) for record-keeping purposes.
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowDeletePropertyModal(false);
                  setPropertyToDelete(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProperty}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Yes, Delete Property
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Property Modal */}
      {showAddPropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Add Manual Property/Account</h3>
              <button
                onClick={() => setShowAddPropertyModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Enter Full Property Address no comma *</label>
                <input
                  type="text"
                  value={newProperty.property_address}
                  onChange={(e) => setNewProperty({...newProperty, property_address: e.target.value})}
                  placeholder="123 Main St"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Property Identifier / Lot Number</label>
                <input
                  type="text"
                  value={newProperty.property_identifier}
                  onChange={(e) => setNewProperty({...newProperty, property_identifier: e.target.value})}
                  placeholder="Lot007"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Account Holder Names (up to 3)</label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newProperty.account_holder_names[0] || ''}
                    onChange={(e) => {
                      const names = [...newProperty.account_holder_names];
                      names[0] = e.target.value;
                      setNewProperty({...newProperty, account_holder_names: names});
                    }}
                    placeholder="Account 1 (e.g., John Doe)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    value={newProperty.account_holder_names[1] || ''}
                    onChange={(e) => {
                      const names = [...newProperty.account_holder_names];
                      names[1] = e.target.value;
                      setNewProperty({...newProperty, account_holder_names: names});
                    }}
                    placeholder="Account 2 (e.g., Jane Doe) - Optional"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    value={newProperty.account_holder_names[2] || ''}
                    onChange={(e) => {
                      const names = [...newProperty.account_holder_names];
                      names[2] = e.target.value;
                      setNewProperty({...newProperty, account_holder_names: names});
                    }}
                    placeholder="Additional Name (Person 3) - Optional"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={newProperty.notes}
                  onChange={(e) => setNewProperty({...newProperty, notes: e.target.value})}
                  placeholder="Additional notes..."
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  onClick={() => setShowAddPropertyModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddProperty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Property
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Forward Modal */}
      {showBalanceForwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold mb-4">Add Balance Forward</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property *
                </label>
                <select
                  value={balanceForwardData.property_address}
                  onChange={(e) => setBalanceForwardData({...balanceForwardData, property_address: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select property...</option>
                  {properties.map(p => (
                    <option key={p.property_address} value={p.property_address}>
                      {p.property_address}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={balanceForwardData.amount}
                  onChange={(e) => setBalanceForwardData({...balanceForwardData, amount: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={balanceForwardData.description}
                  onChange={(e) => setBalanceForwardData({...balanceForwardData, description: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Balance Forward"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={balanceForwardData.due_date}
                  onChange={(e) => setBalanceForwardData({...balanceForwardData, due_date: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBalanceForwardModal(false);
                  setBalanceForwardData({
                    property_address: '',
                    amount: '',
                    description: 'Balance Forward',
                    due_date: new Date().toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBalanceForward}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
              >
                Add Balance Forward
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Confirmation Modal */}
      {/* Export Type Selection Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Export Ledger CSV</h3>
            <p className="text-sm text-gray-600 mb-4">Choose export type:</p>
            
            <div className="space-y-3 mb-4">
              <button
                onClick={() => {
                  handleExportCSV(null, 'summary');
                  setShowExportModal(false);
                }}
                className="w-full text-left px-4 py-3 border-2 rounded-lg hover:bg-gray-50 hover:border-blue-500"
              >
                <div className="font-semibold text-gray-900">📊 Summary Export</div>
                <div className="text-xs text-gray-600 mt-1">
                  Property-level totals (Balance Forward, Current Dues, Late Fees, etc.)
                  <br/>Best for: Overview, reporting, property management
                </div>
              </button>
              
              <button
                onClick={() => {
                  handleExportCSV(null, 'detailed');
                  setShowExportModal(false);
                }}
                className="w-full text-left px-4 py-3 border-2 rounded-lg hover:bg-gray-50 hover:border-blue-500"
              >
                <div className="font-semibold text-gray-900">📝 Detailed Export</div>
                <div className="text-xs text-gray-600 mt-1">
                  Individual transaction line items with Entry IDs
                  <br/>Best for: Line-item editing, detailed analysis, re-importing changes
                </div>
              </button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="text-xs text-blue-900">
                <div className="font-semibold mb-1">💡 How Entry IDs Work (Detailed Export):</div>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Blank Entry ID:</strong> System creates a NEW transaction</li>
                  <li><strong>Existing Entry ID:</strong> System UPDATES that transaction</li>
                </ul>
                <div className="mt-2 text-blue-800">
                  This allows you to export → edit in Excel → re-import with changes!
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showImportConfirmModal && importValidation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">⚠️ CSV Import Validation</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm font-semibold mb-2">Import Type: <span className="text-blue-700">{importValidation.import_type === 'detailed' ? 'Detailed (Line Items)' : 'Summary (Property Balances)'}</span></p>
              {importValidation.import_type === 'detailed' && (
                <p className="text-xs text-blue-800">
                  💡 <strong>Entry ID Column:</strong> Blank = New entry | Has ID = Update existing
                </p>
              )}
            </div>
            
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                <strong>Total Rows:</strong> {importValidation.total_rows}
              </p>
              <p className="text-green-600 mb-2">
                <strong>Valid Rows:</strong> {importValidation.valid_rows}
              </p>
              {importValidation.import_type === 'detailed' && (
                <>
                  <p className="text-blue-600 mb-2">
                    <strong>Will Add:</strong> {importValidation.to_add || 0} new entries
                  </p>
                  <p className="text-orange-600 mb-2">
                    <strong>Will Update:</strong> {importValidation.to_update || 0} existing entries
                  </p>
                  {importValidation.to_delete > 0 && (
                    <p className="text-red-600 mb-2">
                      <strong>Will Delete:</strong> {importValidation.to_delete} items (manual accounts or entries)
                    </p>
                  )}
                </>
              )}
              {importValidation.has_mismatches && (
                <p className="text-red-600 mb-2">
                  <strong>Mismatches:</strong> {importValidation.mismatches?.length || 0}
                </p>
              )}
              {importValidation.has_errors && (
                <p className="text-red-600 mb-2">
                  <strong>Errors:</strong> {importValidation.errors?.length || 0}
                </p>
              )}
            </div>

            {importValidation.import_type === 'detailed' && importValidation.preview && (
              <div className="mb-4">
                {importValidation.preview.will_add?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="font-bold text-blue-600 mb-2">Preview - New Entries (showing first 10):</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 max-h-60 overflow-y-auto">
                      <div className="text-xs space-y-2">
                        {importValidation.preview.will_add.map((item, idx) => (
                          <div key={idx} className="border-b pb-2">
                            <strong>{item.property_address}</strong> - {item.transaction_type} - ${item.amount} ({item.description || 'No desc'})
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {importValidation.preview.will_update?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="font-bold text-orange-600 mb-2">Preview - Updates (showing first 10):</h3>
                    <div className="bg-orange-50 border border-orange-200 rounded p-3 max-h-60 overflow-y-auto">
                      <div className="text-xs space-y-2">
                        {importValidation.preview.will_update.map((item, idx) => (
                          <div key={idx} className="border-b pb-2">
                            <strong>{item.property_address}</strong> - {item.transaction_type} - ${item.amount} (ID: {item.entry_id})
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {importValidation.has_mismatches && importValidation.mismatches && (
              <div className="mb-4">
                <h3 className="font-bold text-red-600 mb-2">Properties Not Found in System:</h3>
                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-60 overflow-y-auto">
                  <ul className="list-disc list-inside text-sm">
                    {importValidation.mismatches.map((mismatch, idx) => (
                      <li key={idx} className="mb-1">
                        <strong>Row {mismatch.row}:</strong> {mismatch.property_address} 
                        <span className="text-gray-600 ml-2">({mismatch.reason})</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  ⚠️ These properties will be skipped during import unless you force the import.
                </p>
              </div>
            )}
            
            {importValidation.has_errors && importValidation.errors && (
              <div className="mb-4">
                <h3 className="font-bold text-red-600 mb-2">Errors Found:</h3>
                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-60 overflow-y-auto">
                  <ul className="list-disc list-inside text-sm">
                    {importValidation.errors.map((error, idx) => (
                      <li key={idx} className="mb-1">
                        <strong>Row {error.row}:</strong> {error.property_address} 
                        <span className="text-gray-600 ml-2">- {error.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowImportConfirmModal(false);
                  setPendingImportFile(null);
                  setImportValidation(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              {(importValidation.has_mismatches || importValidation.has_errors) && (
                <button
                  onClick={() => proceedWithImport(pendingImportFile, importValidation.import_type, true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Force Import (Skip Errors)
                </button>
              )}
              <button
                onClick={() => proceedWithImport(pendingImportFile, importValidation.import_type, false)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {(importValidation.has_mismatches || importValidation.has_errors) ? 'Import Valid Only' : 'Proceed with Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Balances Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-red-800 mb-4">Reset Ledger Balances</h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-medium">
                This will remove ledger entries for all properties in this HOA. A backup will be created before deletion so this can be undone.
              </p>
              <p className="text-xs text-red-600 mt-1">Budget transactions will NOT be affected.</p>
            </div>

            <div className="space-y-3 mb-4">
              <label className="text-sm font-medium text-gray-700">What to reset:</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="reset-type-all">
                  <input type="radio" name="resetType" value="all" checked={resetType === 'all'} onChange={() => setResetType('all')} />
                  <div>
                    <span className="font-medium text-sm">Remove All Balances</span>
                    <p className="text-xs text-gray-500">Removes all ledger entries — dues, balance forward, payments, fees, everything</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="reset-type-dues">
                  <input type="radio" name="resetType" value="dues_only" checked={resetType === 'dues_only'} onChange={() => setResetType('dues_only')} />
                  <div>
                    <span className="font-medium text-sm">Remove Dues Only</span>
                    <p className="text-xs text-gray-500">Only removes dues charges — keeps balance forward, payments, fines, etc.</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="reset-type-bf">
                  <input type="radio" name="resetType" value="balance_forward_only" checked={resetType === 'balance_forward_only'} onChange={() => setResetType('balance_forward_only')} />
                  <div>
                    <span className="font-medium text-sm">Remove Balance Forward Only</span>
                    <p className="text-xs text-gray-500">Only removes balance forward entries — keeps dues, payments, fines, etc.</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Type RESET to confirm:</label>
              <input
                data-testid="reset-confirmation-input"
                type="text"
                placeholder="Type RESET"
                value={resetConfirmation}
                onChange={(e) => setResetConfirmation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-2 mb-6">
              <button
                data-testid="reset-cancel-btn"
                onClick={() => { setShowResetModal(false); setResetConfirmation(''); setResetType('all'); }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                data-testid="reset-confirm-btn"
                disabled={resetConfirmation !== 'RESET' || resetting}
                onClick={async () => {
                  try {
                    setResetting(true);
                    const { data } = await api.post('/api/ledger/reset-balances', {
                      reset_type: resetType,
                      confirmation: 'RESET',
                    });
                    alert(`${data.message}\n\nA backup was created. Use the Undo button below to restore if needed.`);
                    setResetConfirmation('');
                    setResetType('all');
                    loadAllLedgers();
                    loadResetBackups();
                  } catch (err) {
                    console.error('Reset error:', err);
                    alert(`Reset failed: ${err.response?.data?.detail || err.message}`);
                  } finally {
                    setResetting(false);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetting ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>

            {/* Recent Resets / Undo Section */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-bold text-gray-700 mb-2">Recent Resets (Undo)</h4>
              {loadingBackups ? (
                <p className="text-xs text-gray-500">Loading...</p>
              ) : resetBackups.length === 0 ? (
                <p className="text-xs text-gray-500">No resets have been performed yet.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {resetBackups.map((backup) => (
                    <div key={backup.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border text-xs">
                      <div>
                        <span className="font-medium text-gray-800">
                          {backup.reset_type === 'all' ? 'All Balances' : backup.reset_type === 'dues_only' ? 'Dues Only' : 'Balance Forward'}
                        </span>
                        <span className="text-gray-500 ml-2">
                          ({backup.entries_count} entries) — {new Date(backup.reset_at).toLocaleDateString()} {new Date(backup.reset_at).toLocaleTimeString()}
                        </span>
                        <span className="text-gray-400 ml-1">by {backup.reset_by_email}</span>
                      </div>
                      <button
                        data-testid={`undo-reset-${backup.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Restore ${backup.entries_count} entries from this backup?\n\nThis will re-insert the deleted entries.`)) return;
                          try {
                            const { data } = await api.post(`/api/ledger/restore-from-reset/${backup.id}`);
                            alert(data.message);
                            loadAllLedgers();
                            loadResetBackups();
                          } catch (err) {
                            alert(`Restore failed: ${err.response?.data?.detail || err.message}`);
                          }
                        }}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 whitespace-nowrap ml-2"
                      >
                        Undo
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Reusable component for showing transaction history inline
const PropertyTransactionMiniTable = ({ 
  ledger, 
  property_address, 
  isBoard, 
  user, 
  onLedgerChanged,
  showConsolidationModal,
  setShowConsolidationModal,
  consolidationPreview,
  setConsolidationPreview,
  selectedForConsolidation,
  setSelectedForConsolidation,
  checkingMerges,
  setCheckingMerges,
  showManualMergeModal,
  setShowManualMergeModal,
  loadAllLedgers
}) => {
  // Get hoaTimezone from context
  const { hoaData } = useHOA();
  const hoaTimezone = hoaData?.timezone || 'US/Central';
  
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetScope, setResetScope] = useState('all');
  const [resetYear, setResetYear] = useState(new Date().getFullYear());
  
  // Add/Edit line item state
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [lineItemForm, setLineItemForm] = useState({
    transaction_type: 'dues',
    amount: '',
    description: '',
    due_date: new Date().toISOString().split('T')[0],
    year: new Date().getFullYear()
  });
  
  // Inline edit state
  const [inlineEditEntry, setInlineEditEntry] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState({});
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  const formatDate = (date) => {
    if (!date) return '-';
    return formatLocal(date, hoaTimezone, 'MMM d, yyyy');
  };
  
  const formatDateForInput = (date) => {
    if (!date) return new Date().toISOString().split('T')[0];
    return new Date(date).toISOString().split('T')[0];
  };
  
  const handleResetLateFeesInterest = async () => {
    try {
      const response = await api.post(
        `/api/ledger/${user.hoa_id}/property/${encodeURIComponent(property_address)}/reset-late-interest`,
        {
          scope: resetScope,
          year: resetScope === 'year' ? resetYear : null
        }
      );
      
      alert(response.data.message || 'Late fees and interest reset successfully');
      setShowResetModal(false);
      onLedgerChanged();
    } catch (error) {
      console.error('Error resetting late fees/interest:', error);
      alert(error.response?.data?.detail || 'Failed to reset late fees/interest');
    }
  };
  
  const handleAddLineItem = async () => {
    try {
      if (!lineItemForm.amount || parseFloat(lineItemForm.amount) <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      
      await api.post(
        `/api/ledger/${user.hoa_id}/property/${encodeURIComponent(property_address)}/add-line-item`,
        lineItemForm
      );
      
      alert('✅ Line item added successfully');
      setShowAddLineModal(false);
      setLineItemForm({
        transaction_type: 'dues',
        amount: '',
        description: '',
        due_date: new Date().toISOString().split('T')[0],
        year: new Date().getFullYear()
      });
      onLedgerChanged();
    } catch (error) {
      console.error('Error adding line item:', error);
      alert(error.response?.data?.detail || 'Failed to add line item');
    }
  };
  
  const handleStartInlineEdit = (entry) => {
    setInlineEditEntry(entry.id);
    setInlineEditForm({
      amount: entry.amount,
      description: entry.description,
      due_date: formatDateForInput(entry.due_date)
    });
  };
  
  const handleCancelInlineEdit = () => {
    setInlineEditEntry(null);
    setInlineEditForm({});
  };
  
  const handleSaveInlineEdit = async (entry_id) => {
    try {
      await api.put(
        `/api/ledger/${user.hoa_id}/property/${encodeURIComponent(property_address)}/edit-line-item/${entry_id}`,
        inlineEditForm
      );
      
      alert('✅ Line item updated successfully');
      setInlineEditEntry(null);
      setInlineEditForm({});
      onLedgerChanged();
    } catch (error) {
      console.error('Error updating line item:', error);
      alert(error.response?.data?.detail || 'Failed to update line item');
    }
  };
  
  const handleDeleteLineItem = async (entry_id, entry_type) => {
    if (entry_type === 'payment') {
      alert('Cannot delete payment entries. Use the payment system to void/refund payments.');
      return;
    }
    
    if (!window.confirm('⚠️ Are you sure you want to delete this line item? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(
        `/api/ledger/${user.hoa_id}/property/${encodeURIComponent(property_address)}/delete-line-item/${entry_id}`
      );
      
      alert('✅ Line item deleted successfully');
      onLedgerChanged();
    } catch (error) {
      console.error('Error deleting line item:', error);
      alert(error.response?.data?.detail || 'Failed to delete line item');
    }
  };
  
  // Get unique years from transactions for the year dropdown
  const availableYears = ledger.entries
    ? [...new Set(ledger.entries.map(e => e.due_date ? new Date(e.due_date).getFullYear() : null).filter(Boolean))]
    : [];
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold text-gray-900">Transaction History - {property_address}</h4>
        {isBoard && (
          <button
            onClick={() => setShowResetModal(true)}
            className="text-sm px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Reset Late Fees & Interest
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-blue-50 rounded">
        <div>
          <div className="text-xs text-gray-600">Current Dues</div>
          <div className="font-semibold">{formatCurrency(ledger.current_dues)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600">Late Fees</div>
          <div className="font-semibold text-red-600">{formatCurrency(ledger.late_fees)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600">Interest</div>
          <div className="font-semibold text-purple-600">{formatCurrency(ledger.interest)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600">Total Balance</div>
          <div className="font-bold text-lg">{formatCurrency(ledger.balance_due)}</div>
        </div>
      </div>
      
      {/* Add Line Item Button */}
      {isBoard && (
        <div className="mb-3">
          <button
            onClick={() => setShowAddLineModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-2"
          >
            <span>➕</span> Add Line Item
          </button>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-center">Status</th>
              {isBoard && <th className="px-3 py-2 text-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {ledger.entries && ledger.entries.length > 0 ? (
              ledger.entries.map((entry, idx) => {
                const isEditing = inlineEditEntry === entry.id;
                const canEdit = isBoard && !entry.paid && entry.transaction_type !== 'payment';
                
                return (
                  <tr key={entry.id || idx} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <input
                          type="date"
                          value={inlineEditForm.due_date}
                          onChange={(e) => setInlineEditForm({...inlineEditForm, due_date: e.target.value})}
                          className="px-2 py-1 border rounded text-sm w-full"
                        />
                      ) : (
                        formatDate(entry.due_date)
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        entry.transaction_type === 'payment' ? 'bg-green-100 text-green-700' :
                        entry.transaction_type === 'late_fee' ? 'bg-red-100 text-red-700' :
                        entry.transaction_type === 'interest' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.transaction_type?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <input
                          type="text"
                          value={inlineEditForm.description}
                          onChange={(e) => setInlineEditForm({...inlineEditForm, description: e.target.value})}
                          className="px-2 py-1 border rounded text-sm w-full"
                        />
                      ) : (
                        entry.description || '-'
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      entry.transaction_type === 'payment' ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={inlineEditForm.amount}
                          onChange={(e) => setInlineEditForm({...inlineEditForm, amount: e.target.value})}
                          className="px-2 py-1 border rounded text-sm w-full text-right"
                        />
                      ) : (
                        formatCurrency(entry.amount)
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {entry.paid ? (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                          Paid {entry.paid_date ? `on ${formatDate(entry.paid_date)}` : ''}
                        </span>
                      ) : entry.waived ? (
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                          Waived
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                          Unpaid
                        </span>
                      )}
                    </td>
                    {isBoard && (
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => handleSaveInlineEdit(entry.id)}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelInlineEdit}
                              className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : canEdit ? (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => handleStartInlineEdit(entry)}
                              className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteLineItem(entry.id, entry.transaction_type)}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={isBoard ? 6 : 5} className="px-3 py-4 text-center text-gray-500">
                  No transactions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reset Late Fees & Interest</h3>
            
            <div className="space-y-3 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="all"
                  checked={resetScope === 'all'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="text-blue-600"
                />
                <span className="text-sm">Clear all late fees & interest for this property (all years)</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="year"
                  checked={resetScope === 'year'}
                  onChange={(e) => setResetScope(e.target.value)}
                  className="text-blue-600"
                />
                <span className="text-sm">Clear only for specific year:</span>
              </label>
              
              {resetScope === 'year' && (
                <select
                  value={resetYear}
                  onChange={(e) => setResetYear(e.target.value)}
                  className="ml-6 px-3 py-2 border rounded"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                  {availableYears.length === 0 && (
                    <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                  )}
                </select>
              )}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <strong>Warning:</strong> This will waive (set to $0) all unpaid late fees and interest 
                {resetScope === 'year' ? ` for year ${resetYear}` : ' for all years'}. This action cannot be undone.
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetLateFeesInterest}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm Reset
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Line Item Modal */}
      {showAddLineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add Line Item</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Transaction Type</label>
                <select
                  value={lineItemForm.transaction_type}
                  onChange={(e) => setLineItemForm({...lineItemForm, transaction_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="dues">Dues</option>
                  <option value="late_fee">Late Fee</option>
                  <option value="interest">Interest</option>
                  <option value="fine">Fine/Violation</option>
                  <option value="event">Event Fee</option>
                  <option value="balance_forward">Balance Forward</option>
                  <option value="credit">Credit/Refund</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={lineItemForm.amount}
                  onChange={(e) => setLineItemForm({...lineItemForm, amount: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={lineItemForm.description}
                  onChange={(e) => setLineItemForm({...lineItemForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Optional description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <input
                  type="date"
                  value={lineItemForm.due_date}
                  onChange={(e) => setLineItemForm({...lineItemForm, due_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Year</label>
                <input
                  type="number"
                  value={lineItemForm.year}
                  onChange={(e) => setLineItemForm({...lineItemForm, year: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAddLineModal(false);
                  setLineItemForm({
                    transaction_type: 'dues',
                    amount: '',
                    description: '',
                    due_date: new Date().toISOString().split('T')[0],
                    year: new Date().getFullYear()
                  });
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLineItem}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Line Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consolidation Selection Modal */}
      {showConsolidationModal && consolidationPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">🔧 Fix Duplicate Properties</h3>
              <p className="text-sm text-gray-600 mt-2">
                Found {consolidationPreview.total_properties_affected} properties with split ledger history ({consolidationPreview.total_orphaned_entries} entries)
              </p>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {consolidationPreview.preview_items.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedForConsolidation.includes(item.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedForConsolidation([...selectedForConsolidation, item.user_id]);
                        } else {
                          setSelectedForConsolidation(selectedForConsolidation.filter(id => id !== item.user_id));
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">📧 {item.user_email}</div>
                      <div className="text-sm text-gray-700 mt-1">
                        <strong>Current Address:</strong> {item.current_address}
                      </div>
                      <div className="text-sm text-red-600 mt-2">
                        <strong>⚠️ Found {item.orphaned_count} entries at old address(es):</strong>
                      </div>
                      <ul className="text-sm text-gray-600 ml-4 mt-1">
                        {item.orphaned_addresses.map((addr, i) => (
                          <li key={i}>• {addr}</li>
                        ))}
                      </ul>
                      
                      {/* Show entry details */}
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                          View {item.orphaned_count} entries
                        </summary>
                        <div className="mt-2 space-y-1 ml-4">
                          {Object.entries(item.entries_by_address).map(([addr, entries]) => (
                            <div key={addr} className="bg-white p-2 rounded">
                              <div className="font-medium text-gray-700">From: {addr}</div>
                              {entries.slice(0, 3).map((entry, ei) => (
                                <div key={ei} className="text-gray-600 ml-2">
                                  - {entry.description}: ${entry.amount} ({entry.paid ? '✅ Paid' : '❌ Unpaid'})
                                </div>
                              ))}
                              {entries.length > 3 && (
                                <div className="text-gray-500 ml-2">... and {entries.length - 3} more</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700 mb-4">
                <strong>What will happen:</strong>
                <ul className="ml-4 mt-1">
                  <li>✅ Link all selected entries to correct ledger_property_id</li>
                  <li>✅ Update old addresses to current addresses</li>
                  <li>✅ Preserve all ledger history and amounts</li>
                </ul>
              </div>
              
              <div className="flex justify-between gap-3">
                <button
                  onClick={() => {
                    setShowConsolidationModal(false);
                    setConsolidationPreview(null);
                    setSelectedForConsolidation([]);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  ❌ Cancel
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      if (selectedForConsolidation.length === 0) {
                        alert('Please select at least one property to fix.');
                        return;
                      }
                      
                      setCheckingMerges(true);
                      try {
                        const { data } = await api.post(`/api/ledger/${user.hoa_id}/consolidate-duplicates`, {
                          selected_consolidations: selectedForConsolidation
                        });
                        
                        if (data.consolidations && data.consolidations.length > 0) {
                          const details = data.consolidations.map(c => 
                            `  • ${c.user_email}: ${c.entries_consolidated} entries`
                          ).join('\n');
                          alert(`✅ Fixed ${data.total_properties_fixed} properties!\n\n${details}\n\nTotal: ${data.total_entries_fixed} entries`);
                          loadAllLedgers();
                        }
                        
                        setShowConsolidationModal(false);
                        setConsolidationPreview(null);
                      } catch (error) {
                        alert('❌ Failed: ' + (error.response?.data?.detail || error.message));
                      } finally {
                        setCheckingMerges(false);
                      }
                    }}
                    disabled={selectedForConsolidation.length === 0 || checkingMerges}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingMerges ? '🔄 Fixing...' : `🔧 Fix Selected (${selectedForConsolidation.length})`}
                  </button>
                  
                  <button
                    onClick={async () => {
                      setCheckingMerges(true);
                      try {
                        const { data } = await api.post(`/api/ledger/${user.hoa_id}/consolidate-duplicates`);
                        
                        if (data.consolidations && data.consolidations.length > 0) {
                          const details = data.consolidations.map(c => 
                            `  • ${c.user_email}: ${c.entries_consolidated} entries`
                          ).join('\n');
                          alert(`✅ Fixed ${data.total_properties_fixed} properties!\n\n${details}\n\nTotal: ${data.total_entries_fixed} entries`);
                          loadAllLedgers();
                        }
                        
                        setShowConsolidationModal(false);
                        setConsolidationPreview(null);
                      } catch (error) {
                        alert('❌ Failed: ' + (error.response?.data?.detail || error.message));
                      } finally {
                        setCheckingMerges(false);
                      }
                    }}
                    disabled={checkingMerges}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingMerges ? '🔄 Fixing...' : '✅ Auto-Fix All'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Merge Modal */}
      {showManualMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{zIndex: 9999}}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">🔀 Manual Merge Properties</h3>
              <p className="text-sm text-gray-600 mt-2">
                Select a SOURCE property (entries will move FROM here) and a TARGET property (entries will move TO here)
              </p>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto max-h-[65vh]">
              {/* Debug info */}
              {console.log('[Manual Merge Modal] Rendering with', allLedgerProperties.length, 'properties')}
              
              {/* Source Selection */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">1️⃣ Select SOURCE (merge FROM):</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {allLedgerProperties.length === 0 && (
                    <div className="col-span-2 text-center text-gray-500 py-8">
                      No properties found. Please wait...
                    </div>
                  )}
                  {allLedgerProperties.map((prop, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSourceProperty(prop)}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        sourceProperty?.address === prop.address
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{prop.address}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {prop.entry_count} entries • ${prop.total_amount.toFixed(2)} total
                        {!prop.has_ledger_property_id && <span className="ml-2 text-red-600">⚠️ No ledger ID</span>}
                      </div>
                      {prop.user_email && (
                        <div className="text-xs text-gray-500 mt-1">👤 {prop.user_email}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Selection */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">2️⃣ Select TARGET (merge TO):</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {allLedgerProperties
                    .filter(p => p.address !== sourceProperty?.address)
                    .map((prop, idx) => (
                      <div
                        key={idx}
                        onClick={() => setTargetProperty(prop)}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          targetProperty?.address === prop.address
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                        }`}
                      >
                        <div className="font-medium text-gray-900">{prop.address}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {prop.entry_count} entries • ${prop.total_amount.toFixed(2)} total
                          {prop.has_ledger_property_id && <span className="ml-2 text-green-600">✅ Has ledger ID</span>}
                        </div>
                        {prop.user_email && (
                          <div className="text-xs text-gray-500 mt-1">👤 {prop.user_email}</div>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              {/* Preview */}
              {sourceProperty && targetProperty && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="font-semibold text-blue-900 mb-2">📊 Preview:</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>• All <strong>{sourceProperty.entry_count} entries</strong> from <strong className="text-red-600">{sourceProperty.address}</strong></div>
                    <div>• Will be merged into <strong className="text-green-600">{targetProperty.address}</strong></div>
                    <div>• Total amount moving: <strong>${sourceProperty.total_amount.toFixed(2)}</strong></div>
                    <div>• Target will have: <strong>{sourceProperty.entry_count + targetProperty.entry_count} total entries</strong></div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between gap-3">
              <button
                onClick={() => {
                  setShowManualMergeModal(false);
                  setSourceProperty(null);
                  setTargetProperty(null);
                  setAllLedgerProperties([]);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                ❌ Cancel
              </button>
              
              <button
                onClick={async () => {
                  if (!sourceProperty || !targetProperty) {
                    alert('Please select both source and target properties.');
                    return;
                  }
                  
                  if (!confirm(
                    `⚠️ Confirm Manual Merge?\n\n` +
                    `FROM: ${sourceProperty.address}\n` +
                    `TO: ${targetProperty.address}\n\n` +
                    `This will move ${sourceProperty.entry_count} entries ($${sourceProperty.total_amount.toFixed(2)}).\n\n` +
                    `Continue?`
                  )) {
                    return;
                  }
                  
                  setCheckingMerges(true);
                  try {
                    const { data } = await api.post(`/api/ledger/${user.hoa_id}/manual-merge`, {
                      source_address: sourceProperty.address,
                      target_address: targetProperty.address
                    });
                    
                    alert(`✅ Merged successfully!\n\n${data.entries_merged} entries moved from\n"${data.source_address}"\nto\n"${data.target_address}"`);
                    
                    setShowManualMergeModal(false);
                    setSourceProperty(null);
                    setTargetProperty(null);
                    loadAllLedgers();
                  } catch (error) {
                    alert('❌ Merge failed: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setCheckingMerges(false);
                  }
                }}
                disabled={!sourceProperty || !targetProperty || checkingMerges}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingMerges ? '🔄 Merging...' : '✅ Merge Properties'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LedgerTab;
